<script setup lang="ts">
import { computed } from 'vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import type { Resource } from '@/resources/resource_types'
import ItemHeading from './ItemHeading.vue'
import ItemPassage from './ItemPassage.vue'
import type { AssessmentItem } from './assessment_item_types'
import type { ObjectiveLevel } from './objective_levels'
import { partHeading } from './objective_print'

const props = withDefaults(
  defineProps<{
    item: AssessmentItem
    level: ObjectiveLevel
    isLocked?: boolean
  }>(),
  { isLocked: false }
)

const resourcesByUuid = computed(() =>
  Object.fromEntries(props.item.resources.map((resource) => [resource.uuid, resource]))
)

// A locked prompt shows no resource popovers; the model response keeps them under its own blur.
const promptResources = computed<Record<string, Resource>>(() => (props.isLocked ? {} : resourcesByUuid.value))

const hasPassages = computed(() => props.item.passages.length > 0)
const hasMultipleParts = computed(() => props.item.parts.length > 1)

</script>

<template>
  <div class="grid gap-x-8" :class="{ 'md:grid-cols-2': hasPassages }">
    <div v-if="hasPassages" data-testid="item-passages">
      <ItemPassage
        v-for="(passage, index) in item.passages"
        :key="index"
        :passage="passage"
        :is-locked="isLocked"
      />
    </div>

    <div>
      <div v-for="(part, index) in item.parts" :key="index" class="mb-6" data-testid="item-prompt">
        <h4 v-if="hasMultipleParts" class="mb-2.5 mt-6 text-text-xl font-medium">{{ partHeading(index) }}</h4>
        <WikiText :text="part.prompt" :resources="promptResources" :locked="isLocked" />
      </div>

      <div class="mt-9">
        <ItemHeading :level="level">Model Student Response</ItemHeading>
        <div v-for="(part, index) in item.parts" :key="index" data-testid="item-response">
          <h4 v-if="hasMultipleParts" class="mb-2.5 mt-6 text-text-xl font-medium">{{ partHeading(index) }}</h4>
          <WikiText :text="part.response" :resources="resourcesByUuid" :locked="isLocked" />
        </div>
      </div>
    </div>
  </div>
</template>
